import { useQueryClient } from '@tanstack/react-query';
import {
  ConfirmInvoiceInput,
  CreateInvoiceManuallyInput,
  EditInvoiceInput,
  useConfirmInvoiceMutation,
  useCreateCustomerMutation,
  useCreateInvoiceManuallyMutation,
  useCreateVendorMutation,
  useCustomersQuery,
  useEditInvoiceMutation,
  useInvoiceQuery,
  useUpdateCustomerMutation,
  useUpdateVendorMutation,
  useVendorsQuery,
} from '../../../api/gql';
import { useCustomerLookup } from '../../../hooks/useCustomerLookup';
import { useVendorLookup } from '../../../hooks/useVendorLookup';
import { toCentesimal, toDateString } from '../../../utils/transform';
import { isCreateCustomerResponse, isCreateVendorResponse } from '../../../utils/typeGuards';
import { InvoiceFormType, InvoiceManagementInvoiceFormType } from '../../forms/invoice/types';

type Props = {
  onSuccess: (id: string) => void;
  onError: (status: number) => void;
};

export const useInvoiceDataConfirmation = (props: Props) => {
  const handleMutationError = ({ code, message }: { code: number; message: string }) => {
    console.error(message);
    props.onError(code);
  };
  const queryClient = useQueryClient();
  const { mutate: confirmInvoiceMutate, isPending } = useConfirmInvoiceMutation({
    onSuccess: ({ confirmInvoice: { id } }) => props.onSuccess(id),
    onError: handleMutationError,
    onSettled: () => {
      //invalidate customer and vendor queries in case customer or vendor is already created
      queryClient.invalidateQueries({ queryKey: useCustomersQuery.getKey() });
      queryClient.invalidateQueries({ queryKey: useVendorsQuery.getKey() });
    },
  });

  const { mutateAsync: createInvoiceManagementInvoiceAsync } = useCreateInvoiceManuallyMutation({
    onSuccess: ({ createInvoiceManually: { id } }) => props.onSuccess(id),
    onError: handleMutationError,
    onSettled: () => {
      //invalidate customer and vendor queries in case customer or vendor is already created
      queryClient.invalidateQueries({ queryKey: useCustomersQuery.getKey() });
      queryClient.invalidateQueries({ queryKey: useVendorsQuery.getKey() });
    },
  });

  const { mutateAsync: createCustomerAsync } = useCreateCustomerMutation({
    onError: handleMutationError,
  });
  const { mutateAsync: updateCustomerAsync } = useUpdateCustomerMutation({
    onError: handleMutationError,
  });
  const { mutateAsync: createVendorAsync } = useCreateVendorMutation({
    onError: handleMutationError,
  });
  const { mutateAsync: updateVendorAsync } = useUpdateVendorMutation({
    onError: handleMutationError,
  });
  const { mutateAsync: editInvoiceAsync, isPending: isEditInvoicePending } = useEditInvoiceMutation({
    onError: handleMutationError,
    onSuccess: ({ editInvoice: { id } }) => {
      queryClient.invalidateQueries({ queryKey: useInvoiceQuery.getKey({ id }) });
    },
  });
  const {
    lookupCustomerByVatId,
    isReady: isCustomerLookupReady,
    isLoading: isCustomerLookupLoading,
  } = useCustomerLookup();
  const { lookupVendorByVatId, isReady: isVendorLookupReady, isLoading: isVendorLookupLoading } = useVendorLookup();

  const updateCustomer = (customerData: Omit<InvoiceFormType['customer'] & { id: string }, 'remindingTone'>) => {
    return updateCustomerAsync({
      customer: {
        ...customerData,
        name: customerData?.name ?? '', // name should always be filled based on validation schemas
      },
    });
  };

  const createOrUpdateCustomer = (
    customerData: Omit<InvoiceFormType['customer'] & { id?: string }, 'remindingTone'>,
  ) => {
    // if there's vatId that match a contact, update the customer
    if (customerData?.vatId) {
      const contact = lookupCustomerByVatId(customerData.vatId);
      if (contact) {
        return updateCustomer({
          ...contact,
          ...customerData,
        });
      }
    }
    // remove id from customerData if there is one
    const { id: _, ...createCustomerData } = customerData;
    return createCustomerAsync({
      customer: {
        ...createCustomerData,
        name: customerData?.name ?? '', // name should always be filled based on validation schemas
      },
    });
  };

  const updateVendor = (vendorData: InvoiceFormType['vendor'] & { id: string }) => {
    return updateVendorAsync({
      vendor: {
        ...vendorData,
        name: vendorData?.name ?? '', // name should always be filled based on validation schemas
      },
    });
  };

  const createOrUpdateVendor = (vendorData: InvoiceFormType['vendor'] & { id?: string }) => {
    // if there's vatId that match a contact, update the customer
    if (vendorData?.vatId) {
      const contact = lookupVendorByVatId(vendorData.vatId);

      if (contact) {
        return updateVendor({
          ...contact,
          ...vendorData,
        });
      }
    }
    // remove id from vendorData if there is one
    const { id: _, ...createVendorData } = vendorData;
    return createVendorAsync({
      vendor: {
        ...createVendorData,
        name: vendorData?.name ?? '', // name should always be filled based on validation schemas
      },
    });
  };

  const createInvoiceManagementInvoice = async (data: InvoiceManagementInvoiceFormType) => {
    if (
      data.amount === undefined ||
      data.amount === null ||
      data.number === undefined ||
      data.number === null ||
      !data.customer?.name ||
      !data.vendor?.name ||
      !data.currency
    ) {
      throw new Error('invalid confirm invoice input');
    }

    try {
      //create or update customer
      const upsertCustomerRes = await createOrUpdateCustomer(data.customer);
      const customerId = isCreateCustomerResponse(upsertCustomerRes)
        ? upsertCustomerRes.createCustomer.id
        : upsertCustomerRes.updateCustomer.id;
      //create or update vendor
      const upsertVendorRes = await createOrUpdateVendor(data.vendor);
      const vendorId = isCreateVendorResponse(upsertVendorRes)
        ? upsertVendorRes.createVendor.id
        : upsertVendorRes.updateVendor.id;

      const dto: CreateInvoiceManuallyInput = {
        number: data.number,
        amount: toCentesimal(data.amount ?? 0),
        bankAccount: data.bankAccount,
        bankNumber: data.bankNumber,
        currency: data.currency,
        taxPayer: data.taxPayer,
        amountWithoutVat: data.amountWithoutVat !== null ? toCentesimal(data.amountWithoutVat) : null,
        amountVat: data.amountVat !== null ? toCentesimal(data.amountVat) : null,
        customerId: customerId,
        vendorId: vendorId,
        issueDate: data.issueDate ? toDateString(data.issueDate.toString()) : null,
        dueDate: data.dueDate ? toDateString(data.dueDate.toString()) : null,
        taxPoint: data.taxPoint ? toDateString(data.taxPoint.toString()) : null,
        iban: data.iban,
        swift: data.swift,
        variableSymbol: data.variableSymbol,
        items:
          data.items?.map((item) => ({
            ...item,
            pricePerUnit: toCentesimal(item.pricePerUnit),
            priceSum: toCentesimal(item.priceSum),
            priceWithoutVat: item.priceWithoutVat !== undefined ? toCentesimal(item.priceWithoutVat) : undefined,
            vatRate:
              item.vatRate !== undefined && item.vatRate !== null
                ? typeof item.vatRate === 'string'
                  ? item.vatRate
                  : item.vatRate.toString()
                : undefined,
          })) ?? [],
      };

      createInvoiceManagementInvoiceAsync({
        input: dto,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const updateInvoiceManagementInvoice = async (invoiceId: string, data: InvoiceManagementInvoiceFormType) => {
    if (
      data.amount === undefined ||
      data.amount === null ||
      data.number === undefined ||
      data.number === null ||
      !data.customer?.name ||
      !data.vendor?.name ||
      !data.currency
    ) {
      throw new Error('invalid confirm invoice input');
    }

    try {
      //create or update customer
      const upsertCustomerRes = await createOrUpdateCustomer(data.customer);
      const customerId = isCreateCustomerResponse(upsertCustomerRes)
        ? upsertCustomerRes.createCustomer.id
        : upsertCustomerRes.updateCustomer.id;
      //create or update vendor
      const upsertVendorRes = await createOrUpdateVendor(data.vendor);
      const vendorId = isCreateVendorResponse(upsertVendorRes)
        ? upsertVendorRes.createVendor.id
        : upsertVendorRes.updateVendor.id;

      const dto: EditInvoiceInput = {
        id: invoiceId,
        number: data.number,
        amount: toCentesimal(data.amount ?? 0),
        bankAccount: data.bankAccount,
        bankNumber: data.bankNumber,
        currency: data.currency,
        taxPayer: data.taxPayer,
        amountWithoutVat: data.amountWithoutVat !== null ? toCentesimal(data.amountWithoutVat) : null,
        amountVat: data.amountVat !== null ? toCentesimal(data.amountVat) : null,
        customerId: customerId,
        vendorId: vendorId,
        issueDate: data.issueDate ? toDateString(data.issueDate.toString()) : null,
        dueDate: data.dueDate ? toDateString(data.dueDate.toString()) : null,
        taxPoint: data.taxPoint ? toDateString(data.taxPoint.toString()) : null,
        iban: data.iban,
        swift: data.swift,
        variableSymbol: data.variableSymbol,
        items:
          data.items?.map((item) => ({
            ...item,
            pricePerUnit: toCentesimal(item.pricePerUnit),
            priceSum: toCentesimal(item.priceSum),
            priceWithoutVat: item.priceWithoutVat !== undefined ? toCentesimal(item.priceWithoutVat) : undefined,
            vatRate:
              item.vatRate !== undefined && item.vatRate !== null
                ? typeof item.vatRate === 'string'
                  ? item.vatRate
                  : item.vatRate.toString()
                : undefined,
          })) ?? [],
      };

      await editInvoiceAsync({ input: dto });
    } catch (e) {
      console.error(e);
    }
  };

  const confirmInvoice = async (invoiceId: string, data: InvoiceFormType) => {
    if (
      !data.amount ||
      !data.amountDue ||
      !data.number ||
      !data.customer?.name ||
      !data.vendor?.name ||
      !data.currency
    ) {
      throw new Error('invalid confirm invoice input');
    }

    try {
      //create or update customer
      const upsertCustomerRes = await createOrUpdateCustomer(data.customer);
      const customerId = isCreateCustomerResponse(upsertCustomerRes)
        ? upsertCustomerRes.createCustomer.id
        : upsertCustomerRes.updateCustomer.id;
      //create or update vendor
      const upsertVendorRes = await createOrUpdateVendor(data.vendor);
      const vendorId = isCreateVendorResponse(upsertVendorRes)
        ? upsertVendorRes.createVendor.id
        : upsertVendorRes.updateVendor.id;

      const dto: ConfirmInvoiceInput = {
        id: invoiceId,
        number: data.number,
        amount: data.amount,
        amountDue: data.amountDue,
        bankAccount: data.bankAccount,
        bankNumber: data.bankNumber,
        currency: data.currency,
        customerId: customerId,
        vendorId: vendorId,
        issueDate: data.issueDate ? toDateString(data.issueDate.toString()) : null,
        dueDate: data.dueDate ? toDateString(data.dueDate.toString()) : null,
        iban: data.iban,
        swift: data.swift,
        variableSymbol: data.variableSymbol,
        items:
          data.items?.map((item) => ({
            ...item,
            pricePerUnit: toCentesimal(item.pricePerUnit),
            priceSum: toCentesimal(item.priceSum),
          })) ?? [],
      };

      // confirm invoice data
      confirmInvoiceMutate({
        input: dto,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    confirmInvoice,
    createInvoiceManagementInvoice,
    updateInvoiceManagementInvoice,
    state: {
      isLoading: isPending || isCustomerLookupLoading || isVendorLookupLoading || isEditInvoicePending,
      isReady: isVendorLookupReady && isCustomerLookupReady,
    },
  };
};
